import { msg } from '@gonfalon/messages/msg';

export interface ActivityStep {
  id: string;
  kind: 'user' | 'assistant' | 'system' | 'tool' | 'error';

  title: string;
  preview?: string;
  args?: string;
  content?: string;

  agentSummary?: string;
  isError?: boolean;
  durationMs?: number;
  thinkingMs?: number;
  costUsd?: number;
  tokensIn?: number;
  tokensOut?: number;
  cacheRead?: number;

  timestamp?: string;
  timingKnown?: boolean;
}

export type ToolClass =
  | 'vcs'
  | 'shell'
  | 'search'
  | 'files'
  | 'read'
  | 'write'
  | 'skill'
  | 'flags'
  | 'db'
  | 'api'
  | 'other';

export type ActivityKind = 'repository' | 'explore' | 'edit' | 'shell' | 'skill' | 'flags' | 'data' | 'api' | 'other';

const ACTIVITY_KIND_OF: Record<ToolClass, ActivityKind> = {
  vcs: 'repository',

  search: 'explore',
  files: 'explore',
  read: 'explore',
  write: 'edit',
  shell: 'shell',
  skill: 'skill',
  flags: 'flags',
  db: 'data',
  api: 'api',
  other: 'other',
};

export const activityKindOf = (toolClass: ToolClass): ActivityKind => ACTIVITY_KIND_OF[toolClass];

const VCS_COMMAND = /^(git|gh)$/;
const SEARCH_COMMAND = /^(grep|rg|ag|ack|find)$/;

export const normalizeToolName = (raw: string | undefined): string =>
  (raw ?? '')
    .replace(/^execute_tool\s+/i, '')
    .replace(/^mcp__(.+?)__/, '')
    .trim()
    .toLowerCase();

const firstToken = (text: string | undefined): string => (text ?? '').trim().split(/\s+/)[0]?.toLowerCase() ?? '';

export const classifyTool = (step: Pick<ActivityStep, 'title' | 'preview' | 'args'>): ToolClass => {
  const name = normalizeToolName(step.title);

  const command = firstToken(step.preview || step.args);

  if (name === 'skill') {
    return 'skill';
  }
  if (name === 'bash' || name === 'shell' || name === 'sh') {
    if (VCS_COMMAND.test(command)) {
      return 'vcs';
    }
    if (SEARCH_COMMAND.test(command)) {
      return 'search';
    }
    return 'shell';
  }
  if (name.startsWith('github') || name.startsWith('git_') || VCS_COMMAND.test(name)) {
    return 'vcs';
  }
  if (name.startsWith('launchdarkly')) {
    return 'flags';
  }
  if (name === 'grep' || name === 'toolsearch' || name.includes('search')) {
    return 'search';
  }
  if (name === 'glob' || name === 'ls' || name === 'get_keys') {
    return 'files';
  }
  if (name === 'read' || name.startsWith('read_')) {
    return 'read';
  }
  if (name === 'write' || name === 'edit' || name.startsWith('multiedit')) {
    return 'write';
  }
  if (name.includes('migration') || name.includes('clickhouse') || name.includes('query')) {
    return 'db';
  }
  if (name.startsWith('http') || name.includes('fetch') || name.includes('webfetch')) {
    return 'api';
  }
  return 'other';
};

const readStringField = (raw: string | undefined, keys: readonly string[]): string | undefined => {
  if (!raw) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== 'object') {
    return undefined;
  }
  for (const key of keys) {
    const value: unknown = key in parsed ? Reflect.get(parsed, key) : undefined;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

export const readSkillName = (step: Pick<ActivityStep, 'preview' | 'args'>): string | undefined =>
  readStringField(step.args, ['name', 'skill']) ?? readStringField(step.preview, ['name', 'skill']);

export interface ConversationActivity<S extends ActivityStep = ActivityStep> {
  kind: 'activity';
  id: string;
  activityKind: ActivityKind;

  label: string;

  steps: S[];

  glue: S[];
  toolCount: number;
  errorCount: number;
  hasError: boolean;
  durationMs: number;
}

export interface StandaloneStep<S extends ActivityStep = ActivityStep> {
  kind: 'step';
  id: string;
  step: S;
}

export type TranscriptEntry<S extends ActivityStep = ActivityStep> = ConversationActivity<S> | StandaloneStep<S>;

const isGlue = (step: ActivityStep): boolean => step.kind === 'assistant' && !step.content?.trim();

const finish = <S extends ActivityStep>(activity: ConversationActivity<S>): ConversationActivity<S> => ({
  ...activity,
  toolCount: activity.steps.length,
  errorCount: activity.steps.filter((s) => s.isError).length,
  hasError: activity.steps.some((s) => s.isError) || activity.glue.some((s) => s.isError),
  durationMs: [...activity.steps, ...activity.glue].reduce((total, s) => total + (s.durationMs ?? 0), 0),
  label: activityLabel(activity),
});

const PATH_LIKE = /^\S*\/\S+$/;

const baseName = (path: string): string => path.replace(/\/+$/, '').split('/').pop() || path;

const readFilePath = (step: Pick<ActivityStep, 'args'>): string | undefined =>
  readStringField(step.args, ['file_path', 'path', 'notebook_path']);

const activityKindLabel = (kind: ActivityKind): string => {
  switch (kind) {
    case 'repository':
      return msg('vega.conversation.activity.repository');
    case 'explore':
      return msg('vega.conversation.activity.explore');
    case 'edit':
      return msg('vega.conversation.activity.edit');
    case 'shell':
      return msg('vega.conversation.activity.shell');
    case 'skill':
      return msg('vega.conversation.activity.skill');
    case 'flags':
      return msg('vega.conversation.activity.flags');
    case 'data':
      return msg('vega.conversation.activity.data');
    case 'api':
      return msg('vega.conversation.activity.api');
    case 'other':
      return msg('vega.conversation.activity.other');
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

const singleActivityLabel = (kind: Exclude<ActivityKind, 'skill' | 'other'>): string => {
  switch (kind) {
    case 'repository':
      return msg('vega.conversation.activity.singleRepository');
    case 'explore':
      return msg('vega.conversation.activity.singleExplore');
    case 'edit':
      return msg('vega.conversation.activity.singleEdit');
    case 'shell':
      return msg('vega.conversation.activity.singleShell');
    case 'flags':
      return msg('vega.conversation.activity.singleFlags');
    case 'data':
      return msg('vega.conversation.activity.singleData');
    case 'api':
      return msg('vega.conversation.activity.singleApi');
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

const activityLabel = (activity: ConversationActivity<ActivityStep>): string => {
  if (activity.activityKind === 'skill') {
    const name = activity.steps[0] ? readSkillName(activity.steps[0]) : undefined;
    return name ? msg('vega.conversation.activity.skillNamed', { name }) : activityKindLabel('skill');
  }
  const [only] = activity.steps.length === 1 ? activity.steps : [];
  if (!only) {
    return activityKindLabel(activity.activityKind);
  }
  const emitted = only.agentSummary?.trim();
  const target =
    emitted && PATH_LIKE.test(emitted) ? emitted : activity.activityKind === 'edit' ? readFilePath(only) : undefined;
  if (target) {
    const name = baseName(target);
    if (activity.activityKind === 'edit') {
      return msg('vega.conversation.activity.editedFile', { name });
    }
    if (activity.activityKind === 'other') {
      return msg('vega.conversation.activity.calledToolTarget', {
        name: normalizeToolName(only.title) || msg('vega.conversation.activity.toolFallback'),
        target: name,
      });
    }
    return msg('vega.conversation.activity.withTarget', {
      action: singleActivityLabel(activity.activityKind),
      name,
    });
  }
  if (emitted) {
    return emitted;
  }
  if (activity.activityKind === 'other') {
    const name = normalizeToolName(only.title);
    return name ? msg('vega.conversation.activity.calledTool', { name }) : activityKindLabel('other');
  }
  return singleActivityLabel(activity.activityKind);
};

export const groupStepsIntoActivities = <S extends ActivityStep>(steps: S[]): Array<TranscriptEntry<S>> => {
  const entries: Array<TranscriptEntry<S>> = [];
  let current: ConversationActivity<S> | undefined;

  const flush = () => {
    if (current) {
      entries.push(finish(current));
      current = undefined;
    }
  };

  let pending: S[] = [];
  const claimPending = (activity: ConversationActivity<S>) => {
    activity.glue.push(...pending);
    pending = [];
  };

  for (const step of steps) {
    if (step.kind === 'tool') {
      const activityKind = activityKindOf(classifyTool(step));

      if (!current || current.activityKind !== activityKind || activityKind === 'skill') {
        flush();
        current = {
          kind: 'activity',
          id: `activity-${step.id}`,
          activityKind,
          label: activityKindLabel(activityKind),
          steps: [],
          glue: [],
          toolCount: 0,
          errorCount: 0,
          hasError: false,
          durationMs: 0,
        };
      }
      claimPending(current);
      current.steps.push(step);
      continue;
    }

    if (isGlue(step)) {
      if (current) {
        current.glue.push(step);
      } else {
        pending.push(step);
      }
      continue;
    }

    flush();

    for (const orphan of pending) {
      entries.push({ kind: 'step', id: orphan.id, step: orphan });
    }
    pending = [];
    entries.push({ kind: 'step', id: step.id, step });
  }

  for (const orphan of pending) {
    entries.push({ kind: 'step', id: orphan.id, step: orphan });
  }
  pending = [];

  flush();
  return entries;
};

export interface ThoughtProcess<S extends ActivityStep = ActivityStep> {
  kind: 'thought';
  id: string;

  items: Array<CommandGroup<S> | StandaloneStep<S>>;
  activities: Array<ConversationActivity<S>>;
  toolCount: number;

  reasoningCount: number;

  errorCount: number;
  hasError: boolean;

  durationMs: number;

  startMs?: number;
  endMs?: number;
  thinkingMs: number;
  costUsd: number;
  tokensIn: number;
  tokensOut: number;
  cacheRead: number;

  absorbedStepIds: string[];
}

export type TranscriptTopLevel<S extends ActivityStep = ActivityStep> = ThoughtProcess<S> | StandaloneStep<S>;

const stepStart = (step: ActivityStep): number | undefined => {
  if (!step.timingKnown || !step.timestamp) {
    return undefined;
  }
  const parsed = Date.parse(step.timestamp);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const sum = (steps: ActivityStep[], pick: (step: ActivityStep) => number | undefined): number =>
  steps.reduce((total, step) => total + (pick(step) ?? 0), 0);

export interface CommandGroup<S extends ActivityStep = ActivityStep> {
  kind: 'commands';
  id: string;
  activities: Array<ConversationActivity<S>>;
  toolCount: number;
  errorCount: number;
  hasError: boolean;
}

const pluralActivityLabel = (kind: Exclude<ActivityKind, 'skill'>, count: number): string => {
  switch (kind) {
    case 'repository':
      return msg('vega.conversation.activity.pluralRepository', { count });
    case 'explore':
      return msg('vega.conversation.activity.pluralExplore', { count });
    case 'edit':
      return msg('vega.conversation.activity.pluralEdit', { count });
    case 'shell':
      return msg('vega.conversation.activity.pluralShell', { count });
    case 'flags':
      return msg('vega.conversation.activity.pluralFlags', { count });
    case 'data':
      return msg('vega.conversation.activity.pluralData', { count });
    case 'api':
      return msg('vega.conversation.activity.pluralApi', { count });
    case 'other':
      return msg('vega.conversation.activity.pluralOther', { count });
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

type GroupShape = Pick<CommandGroup, 'toolCount' | 'activities'>;

export const commandGroupKind = (group: GroupShape): ActivityKind | undefined => {
  const [first, ...rest] = group.activities;
  if (!first) {
    return undefined;
  }
  if (first.activityKind === 'skill') {
    return rest.length === 0 ? 'skill' : undefined;
  }
  return rest.every((activity) => activity.activityKind === first.activityKind) ? first.activityKind : undefined;
};

export const commandGroupLabel = (group: GroupShape): string => {
  const kind = commandGroupKind(group);
  if (kind && (group.toolCount === 1 || kind === 'skill')) {
    return group.activities[0]?.label ?? activityKindLabel(kind);
  }
  if (kind) {
    return pluralActivityLabel(kind, group.toolCount);
  }
  return msg('vega.conversation.ranCommands', { count: group.toolCount });
};

const KIND_TOOL_CLASS: Record<ActivityKind, ToolClass> = {
  repository: 'vcs',
  explore: 'search',
  edit: 'write',
  shell: 'shell',
  skill: 'skill',
  flags: 'flags',
  data: 'db',
  api: 'api',
  other: 'other',
};

export const commandGroupToolClass = (group: GroupShape): ToolClass | undefined => {
  const kind = commandGroupKind(group);
  if (!kind) {
    return undefined;
  }
  const classes = new Set(group.activities.flatMap((activity) => activity.steps.map(classifyTool)));
  const [only] = classes.size === 1 ? classes : [];
  return only ?? KIND_TOOL_CLASS[kind];
};

const groupCommands = <S extends ActivityStep>(
  entries: Array<ConversationActivity<S> | StandaloneStep<S>>,
): Array<CommandGroup<S> | StandaloneStep<S>> => {
  const out: Array<CommandGroup<S> | StandaloneStep<S>> = [];
  for (const entry of entries) {
    if (entry.kind === 'step') {
      out.push(entry);
      continue;
    }
    const last = out[out.length - 1];
    if (last?.kind === 'commands') {
      last.activities.push(entry);
      last.toolCount += entry.toolCount;
      last.errorCount += entry.errorCount;
      last.hasError ||= entry.hasError;
    } else {
      out.push({
        kind: 'commands',
        id: `commands-${entry.id}`,
        activities: [entry],
        toolCount: entry.toolCount,
        errorCount: entry.errorCount,
        hasError: entry.hasError,
      });
    }
  }
  return out;
};

const loneOtherCall = <S extends ActivityStep>(group: CommandGroup<S>): boolean =>
  group.toolCount === 1 && commandGroupKind(group) === 'other';

const foldLoneCalls = <S extends ActivityStep>(
  items: Array<CommandGroup<S> | StandaloneStep<S>>,
): Array<CommandGroup<S> | StandaloneStep<S>> => {
  const drop = new Set<number>();
  items.forEach((item, index) => {
    if (item.kind !== 'commands' || !loneOtherCall(item)) {
      return;
    }
    const targetAt = items.findIndex(
      (candidate, candidateIndex) =>
        candidateIndex > index &&
        !drop.has(candidateIndex) &&
        candidate.kind === 'commands' &&
        !loneOtherCall(candidate),
    );
    if (targetAt < 0) {
      return;
    }
    const target = items[targetAt];
    if (target.kind !== 'commands') {
      return;
    }
    target.activities = [...item.activities, ...target.activities];
    target.toolCount += item.toolCount;
    target.errorCount += item.errorCount;
    target.hasError ||= item.hasError;
    drop.add(index);
  });
  return items.filter((_, index) => !drop.has(index));
};

const buildThought = <S extends ActivityStep>(
  entries: Array<ConversationActivity<S> | StandaloneStep<S>>,
  id: string,
  absorbed: S[],
): ThoughtProcess<S> => {
  const activities = entries.filter((item): item is ConversationActivity<S> => item.kind === 'activity');
  const items = foldLoneCalls(groupCommands(entries));
  const steps = [...activities.flatMap((activity) => [...activity.steps, ...activity.glue]), ...absorbed];
  const starts = steps.map(stepStart).filter((value): value is number => value != null);
  const ends = steps
    .map((step) => {
      const start = stepStart(step);
      return start == null ? undefined : start + (step.durationMs ?? 0);
    })
    .filter((value): value is number => value != null);

  const timed = starts.length > 0 && ends.length > 0;

  return {
    kind: 'thought',
    id,
    items,
    activities,
    toolCount: activities.reduce((total, activity) => total + activity.toolCount, 0),
    reasoningCount: entries.length - activities.length,
    errorCount: activities.reduce((total, activity) => total + activity.steps.filter((s) => s.isError).length, 0),
    hasError: activities.some((activity) => activity.hasError),
    durationMs: timed ? Math.max(0, Math.max(...ends) - Math.min(...starts)) : 0,
    startMs: timed ? Math.min(...starts) : undefined,
    endMs: timed ? Math.max(...ends) : undefined,
    thinkingMs: sum(steps, (step) => step.thinkingMs),
    costUsd: sum(steps, (step) => step.costUsd),
    tokensIn: sum(steps, (step) => step.tokensIn),
    tokensOut: sum(steps, (step) => step.tokensOut),
    cacheRead: sum(steps, (step) => step.cacheRead),
    absorbedStepIds: absorbed.map((step) => step.id),
  };
};

const hasProse = (step: ActivityStep): boolean => step.kind === 'assistant' && Boolean(step.content?.trim());

export const groupIntoThoughtProcesses = <S extends ActivityStep>(
  entries: Array<TranscriptEntry<S>>,
): Array<TranscriptTopLevel<S>> => {
  const tops: Array<TranscriptTopLevel<S>> = [];
  let turn: Array<TranscriptEntry<S>> = [];

  const closeTurn = () => {
    if (turn.length === 0) {
      return;
    }
    let responseAt = -1;
    let response: StandaloneStep<S> | undefined;
    for (const [index, entry] of turn.entries()) {
      if (entry.kind === 'step' && hasProse(entry.step)) {
        responseAt = index;
        response = entry;
      }
    }
    const rest = turn.filter((_, index) => index !== responseAt);

    const items = rest.filter((entry) => entry.kind === 'activity' || hasProse(entry.step));
    const absorbed = [
      ...rest.flatMap((entry) => (entry.kind === 'step' ? [entry.step] : [])),
      ...(response && items.length > 0 ? [response.step] : []),
    ];
    if (items.length === 0) {
      tops.push(...turn.filter((entry): entry is StandaloneStep<S> => entry.kind === 'step'));
      turn = [];
      return;
    }
    tops.push(buildThought(items, `thought-${turn[0].id}`, absorbed));
    if (response) {
      tops.push(response);
    }
    turn = [];
  };

  for (const entry of entries) {
    if (entry.kind === 'activity' || entry.step.kind === 'assistant') {
      turn.push(entry);
      continue;
    }
    closeTurn();
    tops.push(entry);
  }
  closeTurn();

  return tops;
};
